import React, { useCallback, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import Button from '@/components/Button';
import Stack from '@/components/Stack';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { testAddress } from './api';
import type { TestAddressResponse } from './types';

const AddressTester = () => {
  const [addressValues, setAddressValues] = useState<any>({});
  const [workerTaxType, setWorkerTaxType] = useState<string>('');
  const [result, setResult] = useState<TestAddressResponse['data'] | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleTest = useCallback(async () => {
    if (!addressValues.zip) {
      setError('Please select an address first');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await testAddress({
        zip: addressValues.zip,
        ...(workerTaxType ? { worker_tax_type: workerTaxType } : {}),
      });
      setResult(response.data);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to test address',
      );
    } finally {
      setLoading(false);
    }
  }, [addressValues.zip, workerTaxType]);

  const handleReset = useCallback((values: any) => {
    setAddressValues(values);
    setResult(null);
    setError(null);
  }, []);

  return (
    <Accordion defaultExpanded={false}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" fontWeight={600}>
          Test Address
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Check if an address would pass region validation for worker signup or
          address change.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 300 }}>
            <AddressAutocomplete
              values={addressValues}
              setValues={setAddressValues as any}
              onReset={handleReset as any}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Worker Tax Type</InputLabel>
            <Select
              value={workerTaxType}
              label="Worker Tax Type"
              onChange={(e) => setWorkerTaxType(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="w2">W2</MenuItem>
              <MenuItem value="1099">1099</MenuItem>
            </Select>
          </FormControl>
          <Button
            a11yLabel="Test address"
            label="Test"
            isLoading={loading}
            onClick={handleTest}
          />
        </Box>

        {addressValues.zip && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Zip: {addressValues.zip} | City: {addressValues.city} | State:{' '}
            {addressValues.state}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Alert severity={result.valid ? 'success' : 'error'} sx={{ mt: 2 }}>
            <Typography variant="body2">{result.message}</Typography>
            {result.suggestion && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                {result.suggestion}
              </Typography>
            )}
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AddressTester;
